import { type ReactNode } from "react";
import { defaultNumberFormat } from "../../components/index.ts";
import type { ComplianceMappingResponse } from "./use-compliance.ts";

/**
 * The coverage summary (task 10.3; design C6; Requirement 4.3). It reports, for the chosen
 * framework: how many of its references carry at least one mapped finding (COVERED) versus how many
 * carry none (UNCOVERED), and the count of UNMAPPED findings (findings whose signal crosswalks to no
 * reference). All three come straight off the server's `coverage` and `unmapped` blocks — nothing is
 * recomputed client-side, so the summary cannot drift from the per-reference table.
 *
 * It also surfaces the framework's explicit `no_current_mapping` note when the server reports one,
 * so a framework with no applicable Oathera signal in v1 reads as a deliberate "no current mapping"
 * rather than an empty mystery (Requirement 1.4 surfaced to the operator).
 *
 * Accessibility: it is a definition list of labelled figures, so each number is announced with its
 * meaning; nothing is conveyed by colour alone.
 */

export interface CoverageSummaryProps {
  readonly mapping: ComplianceMappingResponse;
}

export function CoverageSummary(props: CoverageSummaryProps): ReactNode {
  const { coverage, unmapped } = props.mapping;

  return (
    <section className="cmp-section cmp-coverage" aria-label="Coverage summary">
      <div className="cmp-section__head">
        <h2 className="cmp-section__title">Coverage</h2>
        <p className="cmp-section__note">
          How much of this framework the current findings touch, and how many findings crosswalk to
          no reference at all.
        </p>
      </div>

      <dl className="cmp-coverage__grid">
        <div className="cmp-coverage__cell">
          <dt className="cmp-coverage__label">References covered</dt>
          <dd className="cmp-coverage__value">
            {defaultNumberFormat(coverage.references_covered)}
            <span className="cmp-coverage__of">
              {" "}
              of {defaultNumberFormat(coverage.references_total)}
            </span>
          </dd>
        </div>

        <div className="cmp-coverage__cell">
          <dt className="cmp-coverage__label">References uncovered</dt>
          <dd className="cmp-coverage__value">
            {defaultNumberFormat(coverage.references_uncovered)}
            <span className="cmp-coverage__of">
              {" "}
              of {defaultNumberFormat(coverage.references_total)}
            </span>
          </dd>
        </div>

        <div className="cmp-coverage__cell">
          <dt className="cmp-coverage__label">Unmapped findings</dt>
          <dd className="cmp-coverage__value">{defaultNumberFormat(unmapped.count)}</dd>
        </div>
      </dl>

      {coverage.no_current_mapping !== null && (
        <p className="cmp-coverage__note" role="note">
          No current mapping for this framework: {coverage.no_current_mapping}
        </p>
      )}
    </section>
  );
}
